import { yupResolver } from "@hookform/resolvers/yup";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getUserActionState } from "app/reducers/userSlice";
import { addUser } from "app/thunks/userAPI";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation/index";
import { CreateUserRequest } from "interfaces/requests/Users";
import { ErrorResponse } from "interfaces/responses/Auth";
import { UserRole } from "interfaces/User";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import {
  BUTTON_STYLES,
  BUTTON_TYPES,
  HeaderWithBack,
  Input,
  Spinner,
  useCustomToast,
} from "ui";
import { SessionsForm } from "ui/Sessions/SessionsForm";
import { buildConfigs } from "utils/form/sessions-form";

import { SelectedContainersState, UserData } from "../types";
import { userSchema } from "../validations";

export const CreateUserForm = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector(getUserActionState);
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const [selectedContainers, setSelectedContainers] =
    useState<SelectedContainersState>({
      session1: {},
      session2: {},
    });

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setError,
    watch,
  } = useForm<UserData>({
    mode: "all",
    resolver: yupResolver(userSchema),
  });

  const containersAmountValue = watch("containersAmount");

  const handleBack = () => {
    history.push(NAVIGATOR.users);
  };

  useEffect(() => {
    if (containersAmountValue) {
      setSelectedContainers({
        session1: {},
        session2: {},
      });
    }
  }, [containersAmountValue]);

  const isSession1Valid = useMemo(() => {
    return Object.values(selectedContainers.session1).some((value) => value);
  }, [selectedContainers.session1]);

  const onSubmit = async (data: UserData) => {
    const configs = buildConfigs(selectedContainers);

    const userData: CreateUserRequest = {
      ...data,
      role: UserRole.admin,
      configs,
    };

    try {
      await dispatch(addUser(userData)).unwrap();
      history.push(NAVIGATOR.users);
      showSuccessToast({
        title: "¡Listo!",
        message: "Pudimos crear la nueva cuenta",
      });
    } catch (error) {
      setErrorMessage(error as ErrorResponse);
    }
  };

  const handleWhiteSpaces = (event: any) => {
    event.target.value = event.target.value.replace(/\s+/g, "");
  };

  const setErrorMessage = (error: ErrorResponse) => {
    if (error.code) {
      setError("email", {
        type: "manual",
        message: error.message,
      });
    } else {
      showErrorToast({
        title: "Lo sentimos",
        message: "No pudimos crear la nueva cuenta",
      });
    }
  };

  return (
    <div className="flex-col w-full min-h-screen px-14 pt-16 pb-8 bg-faux-dark-gunmetal">
      <HeaderWithBack text="Creación de cuenta" onBackClick={handleBack} />

      <p className="mb-6 text-lg font-semibold">Datos del cliente</p>
      <div className="grid grid-cols-3 gap-x-[6%] gap-y-6 mb-8">
        <Input
          {...register("name")}
          name="name"
          label="Nombre comercial"
          errorMessage={errors.name?.message}
        />
        <Input
          {...register("email")}
          name="email"
          label="Email"
          errorMessage={errors.email?.message}
        />
        <Input
          {...register("phoneNumber")}
          name="phoneNumber"
          label="Teléfono"
          errorMessage={errors.phoneNumber?.message}
          onInput={handleWhiteSpaces}
        />
        <Input
          {...register("country")}
          name="country"
          label="País"
          errorMessage={errors.country?.message}
        />
        <Input
          {...register("city")}
          name="city"
          label="Ciudad"
          errorMessage={errors.city?.message}
        />
        <Input
          {...register("address")}
          name="address"
          label="Dirección"
          errorMessage={errors.address?.message}
        />
        <Input
          {...register("fiscalId")}
          name="fiscalId"
          label="Dato fiscal"
          errorMessage={errors.fiscalId?.message}
        />
      </div>

      <p className="mb-6 text-lg font-semibold">Datos de Licencia</p>
      <div className="grid grid-cols-3 gap-x-[6%]">
        <Input
          {...register("slotsAmount")}
          name="slotsAmount"
          label="Cascos habilitados"
          errorMessage={errors.slotsAmount?.message}
        />
        <Input
          {...register("containersAmount")}
          name="containersAmount"
          label="Contenedores habilitados"
          errorMessage={errors.containersAmount?.message}
        />
      </div>

      <SessionsForm
        containersAmount={containersAmountValue}
        selectedContainers={selectedContainers}
        setSelectedContainers={setSelectedContainers}
      />
      <div>
        <div className="flex flex-col items-end mt-4 pb-3.5">
          <div className="mb-4">
            {containersAmountValue && !isSession1Valid && (
              <p className="text-sm text-red-500">
                Debe seleccionar al menos un contenedor en la Sesión 1.
              </p>
            )}
          </div>
          <MUIButton
            variant="contained"
            disabled={!isValid || isLoading || !isSession1Valid}
            onClick={handleSubmit(onSubmit)}
            className={classNames(BUTTON_STYLES[BUTTON_TYPES.CONFIRM])}
            type="submit"
          >
            {isLoading ? <Spinner color="white" /> : <p>Guardar</p>}
          </MUIButton>
        </div>
      </div>
    </div>
  );
};
